import React, { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import useGetCompletedOrders from '@hooks/useGetCompletedOrders'
import { useGetUser } from '@hooks/useAuth'
import useBookings from '@hooks/useBookings.hook'
import useSubscriptions from '@hooks/useSubscriptions.hook'
import { getFragmentData } from 'parkdepot-shared/gql'
import { OrderResponseFragment } from '@graphql/queries/getOpenOrders.query'
import CarpayOrderCard from '@components/CarpayOrderCard/CarpayOrderCard.component'
import { carpayOrderDetailRoute } from '@views/Carpay/OpenOrder/route.const'
import BookingCard from '@components/BookingCard/BookingCard.component'
import { SpinnerContainer, Text, Title } from './PastOrders.style'
import Spinner from 'parkdepot-shared/components/Spinner'
import { Booking } from '@customTypes/booking.interface'
import { Subscription } from '@customTypes/subscription.interface'
import { B2COrderResponseFragment } from 'parkdepot-shared/gql/graphql'
import { useTranslation } from 'react-i18next'
import { carpayPaidOrderRoute } from '@views/Carpay/PaidOrder/route.const'

type CombinedItem =
    | (Booking & { type: 'booking' })
    | (Subscription & { type: 'subscription' })
    | (B2COrderResponseFragment & { type: 'order'; start_date: string })

const mergeAndSortByStartDate = (
    bookings: Booking[],
    subscriptions: Subscription[],
    orders: B2COrderResponseFragment[]
): CombinedItem[] => {
    const combined: CombinedItem[] = [
        ...bookings.map((booking) => ({ ...booking, type: 'booking' as const })),
        ...subscriptions.map((subscription) => ({ ...subscription, type: 'subscription' as const })),
        ...orders.map((order) => ({
            ...order,
            type: 'order' as const,
            start_date: order.order.start_date // Extract start_date for sorting
        }))
    ]

    return combined.sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())
}

const PastOrdersContainer = () => {
    const queryparams = new URLSearchParams(location.search)
    const { t } = useTranslation()
    const history = useHistory()
    const { user } = useGetUser()
    const plates = user?.license_plates?.map((lp) => lp.plate) ?? []
    const { data, loading, error } = useGetCompletedOrders({
        variables: { plates },
        fetchPolicy: 'cache-and-network',
        skip: !plates.length
    })
    const { groupBookingsByStatus, loading: bookingsLoading } = useBookings()
    const { groupSubscriptionsByStatus, loading: subscriptionsLoading } = useSubscriptions()

    const bookings = useMemo(() => {
        return [...groupBookingsByStatus().completedBookings, ...groupBookingsByStatus().cancelledBookings]
    }, [groupBookingsByStatus])
    const subscriptions = useMemo(() => {
        return [...groupSubscriptionsByStatus().completedSubscriptions]
    }, [groupSubscriptionsByStatus])

    const combinedList = useMemo(() => {
        const orders = data?.b2c_get_completed_orders.orders || []
        const ordersFragmented = orders.map((resp) => getFragmentData(OrderResponseFragment, resp))
        return mergeAndSortByStartDate(bookings, subscriptions, ordersFragmented)
    }, [data, bookings, subscriptions])

    if (bookingsLoading || subscriptionsLoading) {
        return (
            <SpinnerContainer>
                <Spinner theme="primary" size="xl" />
            </SpinnerContainer>
        )
    }

    if (error) return <div>{t('404.title')}</div>

    if (!combinedList.length) {
        return loading ? (
            <SpinnerContainer>
                <Spinner theme="primary" size="xl" />
            </SpinnerContainer>
        ) : (
            <div>
                <p>
                    <Title>{t('home.noEventsFound')}</Title>
                </p>
                <Text>{t('home.noEventsFoundText')}</Text>
            </div>
        )
    }

    return (
        <>
            {combinedList.map((item) => {
                if (item.type === 'booking') {
                    return (
                        <BookingCard
                            key={item.booking_ref}
                            type="booking"
                            booking={item}
                            lotName={item.lot_name}
                            startDate={item.start_date}
                            endDate={item.end_date}
                            plates={item.plates?.map((lp) => lp.plate) ?? []}
                        />
                    )
                } else if (item.type === 'subscription') {
                    return (
                        <BookingCard
                            key={item.id}
                            type="subscription"
                            subscription={item}
                            lotName={item.lot_name}
                            startDate={item.start_date}
                            endDate={item.end_date}
                            plates={item.plates?.map((lp) => lp) ?? []}
                        />
                    )
                } else {
                    return (
                        <CarpayOrderCard
                            key={item.order.order_id}
                            data={item}
                            isLast
                            onClick={() =>
                                history.push(
                                    `${carpayPaidOrderRoute}?plate=${item.order.plate}&order_id=${
                                        item.order.order_id
                                    }&${queryparams.toString()}`
                                )
                            }
                        />
                    )
                }
            })}
        </>
    )
}

export default PastOrdersContainer
